import { fixedUi } from '@/i18n/fixedUi';
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import { resolveImageSpaceDisplayRawUrl } from '@/pages/ipe/workbench/utils/imageSpaceDisplayUrl';
import { convertListingImageToTiktok } from '@/pages/ipe/workbench/utils/convertListingImageToTiktok';
import { toCanonicalUploadsPath } from '@/tiktok-platform/modules/products/components/publishImageUploadThumbSrc';

export function resolveImageSpacePickUrl(image) {
  return resolveImageSpaceDisplayRawUrl(image) || String(image?.url || image?.src || '').trim();
}

export async function imageSpaceRecordToUploadFile(image, fallbackName = 'image.jpg') {
  const url = resolveImageSpacePickUrl(image);
  if (!url) {
    throw new Error(fixedUi('所选图片缺少可用地址'));
  }
  const blob = await resolveImageUrlToBlob(url);
  if (!blob) {
    throw new Error(fixedUi('无法读取所选图片，请重试'));
  }
  const rawName = String(image?.name || image?.filename || fallbackName).trim() || fallbackName;
  const fileName = rawName.includes('.') ? rawName : `${rawName}.jpg`;
  return new File(
    [blob],
    fileName,
    { type: blob.type || 'image/jpeg', lastModified: Date.now() },
  );
}

/**
 * 图片空间记录 → 发品上传槽位（走 transform-pic / upload-pic，不重复写入图片空间）。
 */
export async function convertImageSpaceToPublishSlot({
  shopId,
  image,
  imageType = 1,
  imageBizType = 0,
  skipTiktokConvert = false,
  useCase = '',
} = {}) {
  const url = resolveImageSpacePickUrl(image);
  const name = String(image?.name || image?.filename || image?.id || 'image').trim();
  if (!url) {
    throw new Error(fixedUi('所选图片缺少可用地址'));
  }

  if (skipTiktokConvert) {
    const durable = toCanonicalUploadsPath(url) || url;
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      previewUrl: durable,
      publicUrl: durable,
      ipeUrl: durable,
      tiktokUrl: '',
    };
  }

  if (!shopId) {
    throw new Error(fixedUi('请先选择店铺后再选择图片'));
  }

  const forPackage = Number(imageBizType) === 1;
  const converted = await convertListingImageToTiktok({
    shopId,
    url,
    imageType,
    nameHint: name,
    forPackage,
    useCase,
  });
  if (!converted?.uri) {
    throw new Error(fixedUi('图片转换失败：未获得平台 uri，请重试'));
  }

  const durable = converted.ipeUrl || converted.publicUrl || toCanonicalUploadsPath(url) || url;
  // 预览只用站内 durable；外包装 private-tag 勿作 previewUrl
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: converted.name || name,
    previewUrl: durable,
    publicUrl: converted.publicUrl || durable,
    ipeUrl: durable,
    tiktokUrl: converted.tiktokUrl,
    // Create Product 需要 Upload Image 返回的 uri（主图/认证图/尺码表图）
    uri: converted.uri || undefined,
  };
}
